import { PackagePrepaidRules } from '../modules/prepaid-balances/index.js';
import {
  CompanySales,
  SalesPackagePage,
  SalesPackagesList,
  SalesPromotionsList,
} from '../modules/sales/index.js';
import { useCompanyScope } from './company-context.js';
import { GatedCompanyScreen } from './gated-company-screen.js';
import type { SalesTermsView, SalesView } from './list-views.js';
import type { ViewSetter } from './view-state.js';

/// Vendas: o histórico (`/sales`) ou a Nova venda (`/sales/new`), ADR 0055.
export function CompanySalesScreen({
  page,
  view,
  onView,
  onNewSale,
  onBack,
  onSchedule,
  onOpenAppointment,
}: {
  page: 'history' | 'new';
  view: SalesView;
  onView: ViewSetter<SalesView>;
  onNewSale: () => void;
  onBack: () => void;
  /// A página do atendimento de onde a Nova venda veio (ADR 0059).
  onOpenAppointment?: ((appointmentId: string) => void) | undefined;
  /// Abre a Agenda com o serviço vendido; só com a Agenda contratada e permissão de agendar.
  onSchedule?: ((saleItemUnitId: string) => void) | undefined;
}) {
  const { can, today, company } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('sales.read') && (page === 'history' || can('sales.create'))}>
      <CompanySales
        page={page}
        capabilities={{
          canCreate: can('sales.create'),
          canCancel: can('sales.cancel'),
          canManagePayments: can('sales.payments.manage'),
          canUseCards: can('financial_cards.use'),
          financeContracted: company.moduleKeys.includes('finance'),
          commissionsEnabled: company.featureKeys.includes('sales.commissions'),
          canReadCommissions: can('sales.commissions.read'),
          canReassignCommissions: can('sales.commissions.reassign'),
        }}
        today={today}
        view={view}
        onView={onView}
        onNewSale={onNewSale}
        onBack={onBack}
        onOpenAppointment={onOpenAppointment}
        {...(onSchedule && company.moduleKeys.includes('scheduling') && can('scheduling.create')
          ? { onSchedule }
          : {})}
      />
    </GatedCompanyScreen>
  );
}

/// Pacotes, no Catálogo (ADR 0055).
export function CompanyPackagesScreen({
  view,
  onView,
  onOpenPackage,
}: {
  view: SalesTermsView;
  onView: ViewSetter<SalesTermsView>;
  onOpenPackage: (packageId: string) => void;
}) {
  const { can, company } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('sales.read')}>
      <SalesPackagesList
        view={view}
        onView={onView}
        units={company.businessUnits ?? []}
        canManage={can('sales.packages.manage')}
        onOpen={onOpenPackage}
      />
    </GatedCompanyScreen>
  );
}

/// Promoções, no Catálogo (ADR 0055).
export function CompanyPromotionsScreen({
  view,
  onView,
}: {
  view: SalesTermsView;
  onView: ViewSetter<SalesTermsView>;
}) {
  const { can, today, company } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('sales.read')}>
      <SalesPromotionsList
        view={view}
        onView={onView}
        units={company.businessUnits ?? []}
        today={today}
        canManage={can('sales.promotions.manage')}
      />
    </GatedCompanyScreen>
  );
}

/// A página do pacote (ADR 0054): cadastro em página própria, porque tem itens, valor no saldo,
/// unidades e regras.
export function CompanySalesPackageScreen({
  packageId,
  onBack,
}: {
  packageId: string;
  onBack: () => void;
}) {
  const { can, today, company } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('sales.read')}>
      <SalesPackagePage
        rulesEditor={
          packageId !== 'new' && can('prepaid_balances.settings.manage') ? (
            <PackagePrepaidRules packageId={packageId} />
          ) : null
        }
        packageId={packageId}
        units={company.businessUnits ?? []}
        today={today}
        canManage={can('sales.packages.manage')}
        balancesContracted={company.moduleKeys.includes('prepaid-balances')}
        onBack={onBack}
      />
    </GatedCompanyScreen>
  );
}
